import { DeleteResponse, SupportListResponse, SupportResponse, AdminMemberResponse, AdminMemberListResponse, AdminOverallResponse, AdminSystemAlertListResponse, AdminSystemAlertResponse, AdminMaterialAliasListResponse, AdminMaterialAliasResponse, AdminMaterialAliasSetListResponse, VenueResponse, CreateVenueBulkResponse, ResolveVenueConflictsResponse, ExhibitionListResponse, ExhibitionResponse, CreateExhibitionBulkResponse, ResolveExhibitionConflictsResponse } from '../../../shared/dto/ResDtoRepository';
import { MetadataForQuery, DeleteSupportReq, UpdateSupportReq, CreateSystemAlertReq, DeleteSystemAlertReq, AdminMemberEmailReq, PromoteMaterialAliasReq, CreateVenueReq, CreateVenueBulkReq, UpdateVenueReq, ResolveVenueConflictsReq, CreateExhibitionReq, CreateExhibitionBulkReq, UpdateExhibitionReq, ResolveExhibitionConflictsReq } from '../../../shared/dto/ReqDtoRepository';
import { SupportSortData, MemberSortData, MemberFilterData } from '../../../shared/dto/EntityRepository';
import { sendApiRequest, sendDeleteApiRequest } from '../../../shared/api/apiConfig';
import { SimpleStringResponse } from '../../../shared/dto/ResDtoRepository';
import { buildMemberListParams } from '../../component/members/buildMemberListParams';
import {
  MaterialAliasSortOrder,
  SortDirection,
} from '../../../shared/enum/EnumRepository';

// ----- Support -----
export const getSupport = async (supportId: string): Promise<SupportResponse> => {
  return sendApiRequest<SupportResponse>('get', `/api/v1/admin/support?supportId=${supportId}`);
};

export const getSupportList = async (aui: string, metaData: MetadataForQuery): Promise<SupportListResponse> => {
  const sortData = metaData.sortData as SupportSortData;
  return sendApiRequest<SupportListResponse>('get', `/api/v1/admin/support/list?page=${metaData.page}&size=${metaData.size}&sortBy=${sortData.sort}&direction=${sortData.direction}`);
};

export const updateSupport = async (aui: string, data: UpdateSupportReq): Promise<SupportResponse> => {
  return sendApiRequest('put', `/api/v1/admin/support?aui=${aui}`, data);
};

export const deleteSupport = async (aui: string, data: DeleteSupportReq) => {
  return sendDeleteApiRequest<DeleteResponse>(`/api/v1/admin/support?aui=${aui}&supportId=${data.supportId}`);
};

// ----- Member -----
export const getMember = async (memberId: string): Promise<AdminMemberResponse> => {
  return sendApiRequest<AdminMemberResponse>('get', `/api/v1/admin/member?memberId=${memberId}`);
};

export const getMemberList = async (metaData: MetadataForQuery): Promise<AdminMemberListResponse> => {
  const sortData = metaData.sortData as MemberSortData;
  const filterData = metaData.filterData as MemberFilterData | undefined;
  const params = buildMemberListParams(
    { page: metaData.page, size: metaData.size, sortData },
    filterData
  );
  const qs = params.toString();
  return sendApiRequest<AdminMemberListResponse>('get', `/api/v1/admin/member/list?${qs}`);
};

export const getOverall = async (): Promise<AdminOverallResponse> => {
  return sendApiRequest<AdminOverallResponse>('get', `/api/v1/admin/overall`);
};

export const sendMemberEmail = async (data: AdminMemberEmailReq): Promise<SimpleStringResponse> => {
  return sendApiRequest<SimpleStringResponse>('post', `/api/v1/admin/member/email`, data);
};

export const convertMemberToGroup = async (memberId: string): Promise<SimpleStringResponse> => {
  return sendApiRequest<SimpleStringResponse>('put', `/api/v1/admin/member/group?memberId=${memberId}`);
};

// ----- System Alert -----
export const getSystemAlertList = async (metaData: MetadataForQuery): Promise<AdminSystemAlertListResponse> => {
  return sendApiRequest<AdminSystemAlertListResponse>('get', `/api/v1/admin/system-alert?page=${metaData.page}&size=${metaData.size}&sort=createdAt,desc`);
};

export const createSystemAlert = async (data: CreateSystemAlertReq): Promise<AdminSystemAlertResponse> => {
  return sendApiRequest<AdminSystemAlertResponse>('post', `/api/v1/admin/system-alert`, data);
};

export const endSystemAlert = async (systemAlertId: string): Promise<AdminSystemAlertResponse> => {
  return sendApiRequest<AdminSystemAlertResponse>('put', `/api/v1/admin/system-alert?systemAlertId=${systemAlertId}`);
};

export const deleteSystemAlert = async (data: DeleteSystemAlertReq) => {
  return sendDeleteApiRequest<SimpleStringResponse>(`/api/v1/admin/system-alert?systemAlertId=${data.systemAlertId}`);
};

// ----- Material Alias -----
export const getMaterialAliasList = async (
  page: number,
  unassignedOnly: boolean,
  size: number,
  sortField: MaterialAliasSortOrder,
  sortDirection: SortDirection,
): Promise<AdminMaterialAliasListResponse> => {
  return sendApiRequest<AdminMaterialAliasListResponse>(
    'get',
    `/api/v1/admin/material-alias/list?page=${page}&size=${size}&sort=${sortField},${sortDirection.toLowerCase()}&unassignedOnly=${unassignedOnly}`,
  );
};

export const getMaterialAliasSetList = async (): Promise<AdminMaterialAliasSetListResponse> => {
  return sendApiRequest<AdminMaterialAliasSetListResponse>(
    'get',
    '/api/v1/admin/material-alias-set/list',
  );
};

export const promoteMaterialAlias = async (
  aliasId: string,
  data: PromoteMaterialAliasReq,
): Promise<AdminMaterialAliasResponse> => {
  return sendApiRequest<AdminMaterialAliasResponse>(
    'post',
    `/api/v1/admin/material-alias/${aliasId}/promote`,
    data,
  );
};

export const assignMaterialAliasSet = async (
  aliasId: string,
  aliasSetId: string,
): Promise<AdminMaterialAliasResponse> => {
  return sendApiRequest<AdminMaterialAliasResponse>(
    'put',
    `/api/v1/admin/material-alias/${aliasId}/alias-set?aliasSetId=${aliasSetId}`,
  );
};

export const deleteMaterialAlias = async (
  aliasId: string,
): Promise<SimpleStringResponse> => {
  return sendDeleteApiRequest<SimpleStringResponse>(
    `/api/v1/admin/material-alias/${aliasId}`,
  );
};

// ----- Venue -----
export { getVenueList, getVenue } from '../../../shared/api/venueApi';

export const createVenue = async (data: CreateVenueReq): Promise<VenueResponse> => {
  return sendApiRequest<VenueResponse>('post', '/api/v1/venue', data);
};

export const createVenueBulk = async (
  data: CreateVenueBulkReq,
): Promise<CreateVenueBulkResponse> => {
  return sendApiRequest<CreateVenueBulkResponse>('post', '/api/v1/venue/bulk', data);
};

export const resolveVenueConflicts = async (
  data: ResolveVenueConflictsReq,
): Promise<ResolveVenueConflictsResponse> => {
  return sendApiRequest<ResolveVenueConflictsResponse>(
    'post',
    '/api/v1/venue/bulk/conflicts',
    data,
  );
};

export const updateVenue = async (
  venueId: string,
  data: UpdateVenueReq,
): Promise<VenueResponse> => {
  return sendApiRequest<VenueResponse>('put', `/api/v1/venue/${venueId}`, data);
};

export const deleteVenue = async (venueId: string): Promise<SimpleStringResponse> => {
  return sendDeleteApiRequest<SimpleStringResponse>(`/api/v1/venue/${venueId}`);
};

// ----- Exhibition -----
export const getExhibitionList = async (
  page: number,
  size: number,
): Promise<ExhibitionListResponse> => {
  return sendApiRequest<ExhibitionListResponse>(
    'get',
    `/api/v1/exhibition/list?page=${page}&size=${size}`,
  );
};

export const getExhibition = async (exhibitionId: number | string): Promise<ExhibitionResponse> => {
  return sendApiRequest<ExhibitionResponse>('get', `/api/v1/exhibition/${exhibitionId}`);
};

export const createExhibition = async (
  data: CreateExhibitionReq,
): Promise<ExhibitionResponse> => {
  return sendApiRequest<ExhibitionResponse>('post', '/api/v1/exhibition', data);
};

export const createExhibitionBulk = async (
  data: CreateExhibitionBulkReq,
): Promise<CreateExhibitionBulkResponse> => {
  return sendApiRequest<CreateExhibitionBulkResponse>('post', '/api/v1/exhibition/bulk', data);
};

export const resolveExhibitionConflicts = async (
  data: ResolveExhibitionConflictsReq,
): Promise<ResolveExhibitionConflictsResponse> => {
  return sendApiRequest<ResolveExhibitionConflictsResponse>(
    'post',
    '/api/v1/exhibition/bulk/conflicts',
    data,
  );
};

export const updateExhibition = async (
  exhibitionId: number | string,
  data: UpdateExhibitionReq,
): Promise<ExhibitionResponse> => {
  return sendApiRequest<ExhibitionResponse>('put', `/api/v1/exhibition/${exhibitionId}`, data);
};

export const deleteExhibition = async (
  exhibitionId: number | string,
): Promise<SimpleStringResponse> => {
  return sendDeleteApiRequest<SimpleStringResponse>(`/api/v1/exhibition/${exhibitionId}`);
};
